import { useEffect, useRef, useState } from "react";
import { CheckCircle2, PenLine } from "lucide-react";
import {
  fetchGatewayView,
  postGatewayJson,
  uploadGatewayFaceMatch,
} from "../../lib/signing/gateway-api";
import type { GatewayView, IdentityStep } from "../../lib/signing/types";
import {
  LiveVideoCaptureModal,
  requestLiveCameraStream,
} from "../LiveVideoCaptureModal";
import {
  RECORD_SECONDS,
  faceCaptureFromFile,
  formatBirthDateInput,
  formatCardSerialInput,
  isCompleteBirthDate,
  isCompleteCardSerial,
  type FaceCapture,
} from "../../lib/face-capture";

function toWesternDigits(value: string): string {
  const eastern = "۰۱۲۳۴۵۶۷۸۹";
  const arabic = "٠١٢٣٤٥٦٧٨٩";
  return value
    .replace(/[۰-۹]/g, (d) => String(eastern.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String(arabic.indexOf(d)));
}

function digitsOnly(value: string): string {
  return toWesternDigits(value).replace(/\D/g, "");
}

const STEPS: Array<{ id: IdentityStep; label: string }> = [
  { id: "collect", label: "کد ملی و تماس" },
  { id: "otp_sent", label: "کد تأیید" },
  { id: "otp_verified", label: "تاریخ تولد و سریال کارت ملی" },
  { id: "civil_verified", label: "ویدیو و امضا" },
  { id: "signed", label: "پایان" },
];

function stepIndex(step: IdentityStep): number {
  if (step === "face_matched") return 3;
  if (step === "signed") return 4;
  const i = STEPS.findIndex((s) => s.id === step);
  return i >= 0 ? i : 0;
}

type Props = {
  contractId: string;
  token: string;
  view: GatewayView;
  onViewChange: (v: GatewayView) => void;
  onSigned?: () => void;
};

export function SigningAuthPanel({
  contractId,
  token,
  view,
  onViewChange,
  onSigned,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nationalId, setNationalId] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [debugCode, setDebugCode] = useState<string | null>(null);
  const [birthDate, setBirthDate] = useState("");
  const [cardSerial, setCardSerial] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [cameraBootstrap, setCameraBootstrap] =
    useState<Promise<MediaStream> | null>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const openLiveCapture = () => {
    setError(null);
    setCameraBootstrap(requestLiveCameraStream());
    setCaptureOpen(true);
  };

  const closeLiveCapture = () => {
    setCaptureOpen(false);
    setCameraBootstrap(null);
  };

  const identityStep = view.identityStep ?? "collect";
  const kycLine = view.kycVideoLine ?? "تأیید درخواست احراز هویت در سامانه نویساین";
  const activeIdx = stepIndex(identityStep);
  const showSuccess =
    view.signer.status === "signed" || identityStep === "signed";

  useEffect(() => {
    if (showSuccess) onSigned?.();
  }, [showSuccess, onSigned]);

  const reload = async () => {
    const next = await fetchGatewayView(contractId, token);
    onViewChange(next);
  };

  const onStartIdentity = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const data = await postGatewayJson<{
        ok: true;
        debugCode?: string;
      }>(contractId, token, "/identity/start", { nationalId, phone });
      setDebugCode(data.debugCode ?? null);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusy(false);
    }
  };

  const onVerifyOtp = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await postGatewayJson(contractId, token, "/identity/verify-otp", {
        code: otp,
      });
      setDebugCode(null);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
      setOtp("");
      await reload().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const onBirthDate = async () => {
    if (busy) return;
    if (!isCompleteBirthDate(birthDate)) {
      setError("تاریخ تولد را کامل و به شکل ۱۳۷۰/۰۶/۲۱ وارد کنید");
      return;
    }
    if (!isCompleteCardSerial(cardSerial)) {
      setError("سریال کارت ملی را کامل (۱۰ نویسه) وارد کنید");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await postGatewayJson(contractId, token, "/identity/birth-date", { birthDate, cardSerial });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusy(false);
    }
  };

  const onFaceMatch = async (capture: FaceCapture) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const data = await uploadGatewayFaceMatch(contractId, token, capture);
      onViewChange(data);
      closeLiveCapture();
      if (data.signer.status === "signed" || data.identityStep === "signed") {
        onSigned?.();
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "احراز هویت ویدیویی ناموفق بود";
      setError(msg);
      // A wrong birth date / card serial sends the session back to that step.
      await reload().catch(() => undefined);
      throw e instanceof Error ? e : new Error(msg);
    } finally {
      setBusy(false);
    }
  };

  const onPickVideo = async (file: File | null) => {
    if (videoInputRef.current) videoInputRef.current.value = "";
    if (!file || busy) return;
    setError(null);
    let capture: FaceCapture;
    try {
      capture = await faceCaptureFromFile(file);
    } catch (e) {
      setError(e instanceof Error ? e.message : "پردازش ویدیو ناموفق بود");
      return;
    }
    await onFaceMatch(capture).catch(() => undefined);
  };

  const onReject = async () => {
    if (busy || !rejectReason.trim()) {
      setError("دلیل رد الزامی است");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await postGatewayJson<GatewayView>(contractId, token, "/act", {
        action: "reject",
        rejectionReason: rejectReason.trim(),
      });
      onViewChange(data);
      setShowReject(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusy(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="signing-success">
        <CheckCircle2 size={32} className="signing-success-icon" aria-hidden />
        <p className="signing-success-title">امضا ثبت شد</p>
        <p className="signing-muted">هویت شما تأیید شد و اقدام با موفقیت در نویساین ثبت شد.</p>
        {view.success?.accessPassword ? (
          <div className="signing-access-box">
            <p className="signing-muted">رمز دسترسی به سند:</p>
            <code dir="ltr">{view.success.accessPassword}</code>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="signing-auth-panel">
      <ol className="signing-steps">
        {STEPS.map((s, i) => {
          const done = i < activeIdx;
          const current = i === activeIdx;
          return (
            <li
              key={s.id}
              className={`signing-step${current ? " current" : ""}${done ? " done" : ""}`}
            >
              <span className="signing-step-num">{done ? "✓" : i + 1}</span>
              {s.label}
            </li>
          );
        })}
      </ol>

      {!view.signer.isMyStage && view.processStatus === "active" ? (
        <p className="signing-muted">
          هنوز نوبت امضای شما نرسیده است؛ پس از اتمام مرحلهٔ قبل ادامه دهید.
        </p>
      ) : null}

      {error ? <p className="signing-error">{error}</p> : null}

      {view.processStatus === "active" &&
      (view.signer.isMyStage ||
        ["otp_sent", "otp_verified", "civil_verified"].includes(identityStep)) ? (
        <div className="signing-form">
          {identityStep === "collect" && view.signer.isMyStage ? (
            <>
              <label className="signing-field">
                کد ملی
                <input
                  inputMode="numeric"
                  value={nationalId}
                  onChange={(e) => setNationalId(digitsOnly(e.target.value))}
                  placeholder="مثلاً 0013542419"
                />
              </label>
              <label className="signing-field">
                شماره تماس
                <input
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(toWesternDigits(e.target.value))}
                  placeholder="09xxxxxxxxx"
                />
              </label>
              <button
                type="button"
                className="primary-btn"
                disabled={busy}
                onClick={() => void onStartIdentity()}
              >
                {busy ? "در حال ارسال…" : "ادامه و دریافت کد"}
              </button>
            </>
          ) : null}

          {identityStep === "otp_sent" ? (
            <>
              <label className="signing-field">
                کد تأیید پیامک
                <input
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(digitsOnly(e.target.value))}
                />
              </label>
              {debugCode ? (
                <p className="signing-debug">حالت توسعه — کد: {debugCode}</p>
              ) : null}
              <button
                type="button"
                className="primary-btn"
                disabled={busy}
                onClick={() => void onVerifyOtp()}
              >
                {busy ? "در حال تأیید…" : "تأیید مالکیت سیم‌کارت"}
              </button>
            </>
          ) : null}

          {identityStep === "otp_verified" ? (
            <>
              <label className="signing-field">
                تاریخ تولد (شمسی)
                <input
                  inputMode="numeric"
                  dir="ltr"
                  autoComplete="bday"
                  value={birthDate}
                  onChange={(e) => setBirthDate(formatBirthDateInput(e.target.value))}
                  placeholder="1370/06/21"
                />
              </label>
              <label className="signing-field">
                سریال پشت کارت ملی هوشمند
                <input
                  dir="ltr"
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  value={cardSerial}
                  onChange={(e) => setCardSerial(formatCardSerialInput(e.target.value))}
                  placeholder="1M20115996"
                />
              </label>
              <p className="signing-muted small">
                تاریخ تولد مطابق کارت ملی و سریال ۱۰ نویسه‌ای پشت کارت؛ همراه ویدیوی شما برای
                استعلام ثبت احوال ارسال می‌شود.
              </p>
              <button
                type="button"
                className="primary-btn"
                disabled={busy || !isCompleteBirthDate(birthDate) || !isCompleteCardSerial(cardSerial)}
                onClick={() => void onBirthDate()}
              >
                {busy ? "در حال ثبت…" : "ادامه"}
              </button>
            </>
          ) : null}

          {identityStep === "civil_verified" ? (
            <>
              <p className="signing-muted">
                یک ویدیوی کوتاه (حداکثر {RECORD_SECONDS} ثانیه) با صدا ضبط کنید و در آن این جمله را
                بلند بخوانید:
              </p>
              <p className="live-capture-line">«{kycLine}»</p>
              <p className="signing-muted">
                چهره با تصویر ثبت احوال و گفتار با همین جمله تطبیق داده می‌شود و پس از تأیید، سند
                به‌صورت خودکار امضا می‌شود.
              </p>
              <button
                type="button"
                className="primary-btn"
                disabled={busy}
                onClick={openLiveCapture}
              >
                {busy ? "در حال احراز هویت…" : "ضبط ویدیو زنده"}
              </button>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime,video/*"
                capture="user"
                className="hidden"
                onChange={(e) => void onPickVideo(e.target.files?.[0] ?? null)}
              />
              <button
                type="button"
                className="ghost-btn live-capture-full"
                disabled={busy}
                onClick={() => videoInputRef.current?.click()}
              >
                ضبط با دوربین گوشی / انتخاب فایل
              </button>
              <p className="signing-muted small">
                ویدیو باید کوتاه (حداکثر ۱۰ مگابایت)، با صدا، و چهره در آن کامل و واضح باشد.
              </p>
              <LiveVideoCaptureModal
                open={captureOpen}
                busy={busy}
                line={kycLine}
                bootstrapStream={cameraBootstrap}
                confirmLabel="تأیید و امضا"
                busyLabel="در حال احراز هویت و امضا…"
                onClose={closeLiveCapture}
                onConfirm={(capture) => onFaceMatch(capture)}
              />
            </>
          ) : null}
        </div>
      ) : null}

      {view.signer.status === "rejected" ? (
        <p className="signing-muted">رد امضا ثبت شد.</p>
      ) : null}

      {view.processStatus === "active" && view.signer.canAct && !showReject ? (
        // rejecting ends the process for every party — the API requires the
        // signer's phone to be verified in this session first
        ["otp_verified", "civil_verified", "face_matched"].includes(identityStep) ? (
          <button
            type="button"
            className="ghost-btn"
            disabled={busy}
            onClick={() => setShowReject(true)}
          >
            رد امضا
          </button>
        ) : (
          <p className="signing-muted small">
            برای رد امضا ابتدا کد ملی و شماره تماس خود را وارد و کد پیامکی را تأیید کنید.
          </p>
        )
      ) : null}

      {showReject ? (
        <div className="signing-reject">
          <textarea
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="دلیل رد"
          />
          <div className="signing-reject-actions">
            <button
              type="button"
              className="danger-btn"
              disabled={busy}
              onClick={() => void onReject()}
            >
              ثبت رد
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowReject(false)}
            >
              انصراف
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function SigningPanelHeader() {
  return (
    <div className="signing-panel-head">
      <PenLine size={18} aria-hidden />
      <div>
        <p className="signing-panel-kicker">نویساین</p>
        <h2 className="signing-panel-title">احراز هویت و امضا</h2>
        <p className="signing-muted" style={{ margin: "0.2rem 0 0" }}>
          مسیر کوتاه و مرحله‌به‌مرحله برای امضای امن
        </p>
      </div>
    </div>
  );
}
